import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, generatePath } from 'react-router-dom';
import { Typography } from 'antd';
import MainLayout from '../../components/MainLayout';
import { AuthContext, AuthContextState } from '../../context/AuthContext';
import { PROBLEM_ROUTE, PROBLEM_SETS_ROUTE } from '../../config/constants';
import { getProblemSets, getProblemStats, ProblemResponse, ProblemSetNestedResponse, ProblemStatsResponse } from '../../api/problems';
import { AxiosResponse } from 'axios';
import Leaderboard from '../../components/Leaderboard';
import BasicProgressStats from '../../components/BasicProgressStats';
import DifficultyProgressStats from '../../components/DifficultyProgressStats';
import ProblemSetsIcon from '../../assets/images/problemsetsicon.png';
import ProblemOfTheDayIcon from '../../assets/images/problemofthedayicon.png';
import LargeButton from '../../components/LargeButton';
import { getProblemOfTheDay } from '../../utils/problems';
import styles from './Dashboard.module.css';

export interface DashboardProps {};

// shows user's solved problems, completed problem sets,
// solved problems by difficulty, leaderboard,
// and includes links to problem sets and problem of the day
const Dashboard = (props: DashboardProps): JSX.Element | null => {
  // user problem stats data
  const [problemStats, setProblemStats] = useState<ProblemStatsResponse>(
    {
      solved_problems_count: 0,
      total_problems_count: 0,
      completed_problemsets_count: 0,
      total_problemsets_count: 0,
      solved_problems_beginner_count: 0,
      total_problems_beginner_count: 0,
      solved_problems_intermediate_count: 0,
      total_problems_intermediate_count: 0,
      solved_problems_advanced_count: 0,
      total_problems_advanced_count: 0,
      top_users: []
    }
  );
  // problem of the day
  const [problemOfTheDay, setProblemOfTheDay] = useState<ProblemResponse | null>(null);
  // ref to avoid fetching problems stats multiple times on multiple renders
  const fetchProblemSetsDataCalled = useRef<boolean>(false);
  // get refresh request function from auth context
  const { refreshRetryRequest } = useContext(AuthContext) as AuthContextState;

  // set document title
  useEffect(() => {
    document.title = 'Dashboard | Sigmoid Academy';
  }, []);

  // fetch user problem stats on render
  useEffect(() => {
    if (fetchProblemSetsDataCalled.current) {
      return;
    }

    fetchProblemSetsDataCalled.current = true;
    // get problem sets
    refreshRetryRequest(
      getProblemSets
    ).then(
      (response: AxiosResponse) => {
        // get problem of the day
        const problemSets: ProblemSetNestedResponse[] = response.data as ProblemSetNestedResponse[];
        const problems: ProblemResponse[] = problemSets.flatMap((ps: ProblemSetNestedResponse) => ps.problems);
        setProblemOfTheDay(getProblemOfTheDay(problems));
      }
    ).catch((error: any) => console.log('Error in getting problem sets'));

    // get problem stats
    refreshRetryRequest(
      getProblemStats
    ).then((response: AxiosResponse) => setProblemStats(response.data as ProblemStatsResponse)
    ).catch((error: any) => console.log('Error in getting problem stats request'));
  }, [refreshRetryRequest]);

  return (
    <div>
      <MainLayout selectedKey='dashboard'>
        <div className={styles.mainContainer}>
          <div className={styles.columnStatsContainer}>
            <div className={styles.leftColumnStatsContainer}>
              <Typography.Title
                className={styles.mainHeading}
                level={2}
              >
                Your Progress
              </Typography.Title>
              <BasicProgressStats
                problemsSolved={problemStats.solved_problems_count}
                totalProblems={problemStats.total_problems_count}
                problemSetsCompleted={problemStats.completed_problemsets_count}
                totalProblemSets={problemStats.total_problemsets_count}
              />
              <DifficultyProgressStats
                beginnerProblemsSolved={problemStats.solved_problems_beginner_count}
                totalBeginnerProblems={problemStats.total_problems_beginner_count}
                intermediateProblemsSolved={problemStats.solved_problems_intermediate_count}
                totalIntermediateProblems={problemStats.total_problems_intermediate_count}
                advancedProblemsSolved={problemStats.solved_problems_advanced_count}
                totalAdvancedProblems={problemStats.total_problems_advanced_count}
              />
            </div>
            <div className={styles.rightColumnStatsContainer}>
              <Typography.Title
                className={styles.mainHeading}
                level={2}
              >
                Leaderboard
              </Typography.Title>
              <Leaderboard users={problemStats.top_users} />
              <Typography.Title
                className={styles.mainHeading}
                level={2}
              >
                Explore
              </Typography.Title>
              <div className={styles.buttonsContainer}>
                <Link to={PROBLEM_SETS_ROUTE}>
                  <LargeButton
                    text={<span>Problem<br />Sets</span>}
                    textLevel={3}
                    textOnRight={false}
                    iconSrc={ProblemSetsIcon}
                    iconAlt={'Problem Sets Icon'}
                    iconSize={90}
                    lightBackground={false}
                  />
                </Link>
                <Link to={problemOfTheDay ? generatePath(PROBLEM_ROUTE, { key: problemOfTheDay.slug ? problemOfTheDay.slug : problemOfTheDay.id.toString() }) : '.'}>
                  <LargeButton
                    text={<span>Problem of<br />the Day</span>}
                    textLevel={3}
                    textOnRight={true}
                    iconSrc={ProblemOfTheDayIcon}
                    iconAlt={'Problem of the Day Icon'}
                    iconSize={90}
                    lightBackground={true}
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    </div>
  );
};

export default Dashboard;
